import React, { useState, useEffect } from 'react';
import { useSidebar } from '../../contexts/SidebarContext';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import clientService from '../../services/clientService';
import NotificationToast from '../../components/ui/NotificationToast';
import { useAuth } from '../../contexts/AuthContext';
import AddClientModal from './components/AddClientModal';
import ClientDetailView from './components/ClientDetailView';

const ClientManagement = () => {
  const { userProfile } = useAuth();
  const { isCollapsed } = useSidebar();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [notification, setNotification] = useState(null);

  const user = {
    name: userProfile?.full_name || "Admin",
    email: userProfile?.email || "admin@facilitypro.com",
    role: userProfile?.role || "Admin"
  };

  useEffect(() => {
    loadClients();
  }, [searchTerm, filterStatus]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (searchTerm) filters.search = searchTerm;
      if (filterStatus !== 'all') filters.status = filterStatus;

      const result = await clientService.getClients(filters);
      if (result?.success) {
        setClients(result.data || []);
      } else {
        setNotification({ message: result?.error || 'Failed to load clients', type: 'error' });
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      setNotification({ message: 'An error occurred while loading clients', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddClient = () => {
    setEditingClient(null);
    setShowAddModal(true);
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setShowAddModal(true);
  };

  const handleDeleteClient = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client? This action cannot be undone.')) return;

    try {
      const result = await clientService.deleteClient(id);
      if (result?.success) {
        setNotification({ message: 'Client deleted successfully', type: 'success' });
        loadClients();
      } else {
        setNotification({ message: result?.error || 'Failed to delete client', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    }
  };

  const handleClientSaved = () => {
    setShowAddModal(false);
    setEditingClient(null);
    loadClients();
  };

  const handleViewClient = (client) => {
    setSelectedClient(client);
  };

  const handleClientUpdated = () => {
    loadClients();
  };

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation
        onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
        user={user}
        notifications={[]}
      />

      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className={`pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-6">
          <BreadcrumbNavigation />

          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Client Management</h1>
              <p className="text-muted-foreground mt-2">
                Manage client companies and their information
              </p>
            </div>
            <Button onClick={handleAddClient} iconName="Plus" iconPosition="left">
              Add Client
            </Button>
          </div>

          <NotificationToast
            message={notification?.message}
            type={notification?.type}
            onDismiss={() => setNotification(null)}
          />

          {/* Filters */}
          <div className="mb-6 flex gap-4 items-center">
            <div className="flex-1 max-w-md">
              <Input
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                iconName="Search"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-border rounded-lg bg-background text-foreground"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* Clients Table */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading clients...</p>
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-12 bg-card border border-border rounded-lg">
              <Icon name="Building2" size={48} className="mx-auto mb-4 opacity-50 text-muted-foreground" />
              <p className="text-muted-foreground">No clients found</p>
              <p className="text-sm text-muted-foreground mt-2">
                {searchTerm ? 'Try adjusting your search criteria' : 'Get started by adding your first client'}
              </p>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Client Code
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Company Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Contact Person
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Phone
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Industry
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {clients.map((client) => (
                      <tr key={client.id} className="hover:bg-muted/50 transition-colors">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <Icon name="Hash" size={14} className="text-muted-foreground" />
                            <span className="text-sm font-mono font-medium text-foreground">
                              {client.client_code || 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <Icon name="Building2" size={20} className="text-primary" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">
                                {client.company_name}
                              </p>
                              {client.gst_number && (
                                <p className="text-xs text-muted-foreground truncate">
                                  GST: {client.gst_number}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <Icon name="User" size={14} className="text-muted-foreground" />
                            <span className="text-sm text-foreground">
                              {client.contact_person || 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          {client.email ? (
                            <div className="flex items-center gap-2">
                              <Icon name="Mail" size={14} className="text-muted-foreground" />
                              <span className="text-sm text-foreground truncate max-w-[200px]">
                                {client.email}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">N/A</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          {client.phone ? (
                            <div className="flex items-center gap-2">
                              <Icon name="Phone" size={14} className="text-muted-foreground" />
                              <span className="text-sm text-foreground">
                                {client.phone}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">N/A</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          {client.industry ? (
                            <div className="flex items-center gap-2">
                              <Icon name="Briefcase" size={14} className="text-muted-foreground" />
                              <span className="text-sm text-foreground">
                                {client.industry}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">N/A</span>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${client.status === 'active'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                            }`}>
                            {client.status || 'active'}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewClient(client)}
                              title="View Details"
                            >
                              <Icon name="Eye" size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditClient(client)}
                              title="Edit"
                            >
                              <Icon name="Edit" size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClient(client.id)}
                              className="text-destructive"
                              title="Delete"
                            >
                              <Icon name="Trash2" size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Client Modal */}
      <AddClientModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingClient(null);
        }}
        client={editingClient}
        onSave={handleClientSaved}
      />

      {/* Client Detail View */}
      {selectedClient && (
        <ClientDetailView
          client={selectedClient}
          onClose={() => setSelectedClient(null)}
          onClientUpdated={handleClientUpdated}
        />
      )}
    </div>
  );
};

export default ClientManagement;

